import { useState, useEffect, useRef } from "react";
import { MainLayout } from "@/components/MainLayout";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  FileCode,
  Image,
  List,
  Copy,
  Check,
  Search,
  Download,
  ZoomIn,
  ExternalLink,
  Server,
  Database,
  Globe,
  Shield,
  Sparkles,
  Loader2,
  Wallet,
  CreditCard,
  PiggyBank,
  TrendingUp,
  Brain,
  Building,
  FileText,
  ShieldCheck,
  ChartLine,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminArchitecturePage = () => {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState("api");
  const [apiDocs, setApiDocs] = useState(null);
  const [features, setFeatures] = useState(null);
  const [diagrams, setDiagrams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [copiedSection, setCopiedSection] = useState(null);
  const [selectedDiagram, setSelectedDiagram] = useState(null);
  const [diagramSvg, setDiagramSvg] = useState(null);
  const [diagramLoading, setDiagramLoading] = useState(false);

  const fetchData = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const [apiRes, featuresRes, diagramsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/architecture/api-docs`, { credentials: "include" }),
        fetch(`${API_URL}/api/admin/architecture/features`, { credentials: "include" }),
        fetch(`${API_URL}/api/admin/architecture/diagrams`, { credentials: "include" }),
      ]);

      if (apiRes.ok) setApiDocs(await apiRes.json());
      if (featuresRes.ok) setFeatures(await featuresRes.json());
      if (diagramsRes.ok) setDiagrams(await diagramsRes.json());
    } catch (error) {
      console.error("Failed to fetch architecture data:", error);
      toast.error("Failed to load architecture documentation");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && isAdmin) {
      fetchData();
    } else if (!authLoading && !isAdmin) {
      setLoading(false);
    }
  }, [isAdmin, authLoading]);

  // Wait for auth to complete before redirecting
  if (authLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  // Redirect non-admins
  if (!isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  const loadDiagram = async (diagramId) => {
    setDiagramLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/architecture/diagrams/${diagramId}`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setDiagramSvg(data.svg);
        setSelectedDiagram(diagramId);
      }
    } catch (error) {
      console.error("Failed to load diagram:", error);
      toast.error("Failed to load diagram");
    } finally {
      setDiagramLoading(false);
    }
  };

  const downloadDiagram = (svgContent, name) => {
    const blob = new Blob([svgContent], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name}.svg`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Diagram downloaded");
  };

  const copyToClipboard = async (text, sectionId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(sectionId);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const formatFeaturesAsText = () => {
    if (!features) return "";
    let text = "CABLEWAY FINANCE - FEATURE LIST\n";
    text += "================================\n\n";
    
    Object.entries(features).forEach(([category, data]) => {
      text += `## ${category}\n`;
      text += `${data.description}\n\n`;
      data.features.forEach((f, i) => {
        text += `  ${i + 1}. ${f}\n`;
      });
      text += "\n";
    });
    
    return text;
  };

  const getMethodColor = (method) => {
    switch (method) {
      case "GET": return "bg-green-500/10 text-green-600 border-green-500/30";
      case "POST": return "bg-blue-500/10 text-blue-600 border-blue-500/30";
      case "PUT": return "bg-yellow-500/10 text-yellow-600 border-yellow-500/30";
      case "DELETE": return "bg-red-500/10 text-red-600 border-red-500/30";
      case "PATCH": return "bg-purple-500/10 text-purple-600 border-purple-500/30";
      default: return "bg-gray-500/10 text-gray-600 border-gray-500/30";
    }
  };

  const getGroupIcon = (iconName) => {
    const icons = {
      "shield": Shield,
      "chart": ChartLine,
      "wallet": Wallet,
      "credit-card": CreditCard,
      "piggy-bank": PiggyBank,
      "sparkles": Sparkles,
      "trending-up": TrendingUp,
      "brain": Brain,
      "building": Building,
      "file-text": FileText,
      "shield-check": ShieldCheck,
      "server": Server,
    };
    return icons[iconName] || Server;
  };

  // Filter API endpoints by search (updated for new structure)
  const filteredApiDocs = apiDocs?.groups ? Object.entries(apiDocs.groups).reduce((acc, [group, data]) => {
    const filtered = data.endpoints.filter(ep => 
      ep.path.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ep.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
      group.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (filtered.length > 0) {
      acc[group] = { ...data, endpoints: filtered, count: filtered.length };
    }
    return acc;
  }, {}) : {};

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="admin-architecture-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">
              Architecture Documentation
            </h1>
            <p className="text-muted-foreground mt-1">
              API endpoints, system diagrams, and feature documentation
            </p>
          </div>
          <Badge variant="outline" className="gap-1 w-fit">
            <Shield className="w-3 h-3" /> Admin Only
          </Badge>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="api" className="gap-2">
              <FileCode className="w-4 h-4" /> API Docs
            </TabsTrigger>
            <TabsTrigger value="diagrams" className="gap-2">
              <Image className="w-4 h-4" /> Diagrams
            </TabsTrigger>
            <TabsTrigger value="features" className="gap-2">
              <List className="w-4 h-4" /> Features
            </TabsTrigger>
          </TabsList>

          {/* API Documentation Tab */}
          <TabsContent value="api" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Server className="w-5 h-5" />
                      API Endpoints
                    </CardTitle>
                    <CardDescription>
                      {apiDocs?.total_endpoints || 0} endpoints organized by category
                    </CardDescription>
                  </div>
                  <div className="relative w-full md:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search endpoints..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Accordion type="multiple" className="w-full">
                  {Object.entries(filteredApiDocs).map(([groupName, groupData]) => {
                    const IconComponent = getGroupIcon(groupData.icon);
                    const endpoints = groupData.endpoints || [];
                    
                    return (
                    <AccordionItem key={groupName} value={groupName}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <IconComponent className="w-4 h-4 text-muted-foreground" />
                          <span className="font-semibold text-sm">{groupName}</span>
                          <Badge variant="secondary">{endpoints.length}</Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        {groupData.description && (
                          <p className="text-sm text-muted-foreground mb-3 px-1">{groupData.description}</p>
                        )}
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-24">Method</TableHead>
                              <TableHead>Endpoint</TableHead>
                              <TableHead className="hidden md:table-cell">Description</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {endpoints.map((ep, idx) => (
                              <TableRow key={idx}>
                                <TableCell>
                                  <Badge variant="outline" className={`font-mono text-xs ${getMethodColor(ep.method)}`}>
                                    {ep.method}
                                  </Badge>
                                </TableCell>
                                <TableCell className="font-mono text-sm">{ep.path}</TableCell>
                                <TableCell className="hidden md:table-cell text-muted-foreground text-sm">
                                  {ep.summary}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </AccordionContent>
                    </AccordionItem>
                    );
                  })}
                </Accordion>

                <div className="mt-6 pt-4 border-t flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Interactive documentation available at <code className="bg-secondary px-2 py-0.5 rounded">/api/docs</code>
                  </p>
                  <Button variant="outline" size="sm" asChild>
                    <a href={`${API_URL}/api/docs`} target="_blank" rel="noopener noreferrer" className="gap-2">
                      <ExternalLink className="w-4 h-4" /> Open Swagger UI
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Diagrams Tab */}
          <TabsContent value="diagrams" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {diagrams.map((diagram) => (
                <Card key={diagram.id} className="overflow-hidden hover:border-primary/50 transition-colors">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{diagram.name}</CardTitle>
                    <CardDescription>{diagram.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => loadDiagram(diagram.id)}
                      >
                        <ZoomIn className="w-4 h-4" /> View Full Size
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Diagram Modal */}
            <Dialog open={!!selectedDiagram} onOpenChange={() => { setSelectedDiagram(null); setDiagramSvg(null); }}>
              <DialogContent className="max-w-5xl max-h-[90vh] overflow-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center justify-between">
                    <span>{diagrams.find(d => d.id === selectedDiagram)?.name}</span>
                    {diagramSvg && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => downloadDiagram(diagramSvg, selectedDiagram)}
                      >
                        <Download className="w-4 h-4" /> Download SVG
                      </Button>
                    )}
                  </DialogTitle>
                </DialogHeader>
                <div className="mt-4 bg-white rounded-lg p-4 border">
                  {diagramLoading ? (
                    <div className="flex items-center justify-center h-64">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  ) : diagramSvg ? (
                    <div dangerouslySetInnerHTML={{ __html: diagramSvg }} />
                  ) : null}
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Features Tab */}
          <TabsContent value="features" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5" />
                      Platform Features
                    </CardTitle>
                    <CardDescription>
                      Complete list of Cableway Finance features
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => copyToClipboard(formatFeaturesAsText(), "all-features")}
                  >
                    {copiedSection === "all-features" ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                    Copy All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {features && Object.entries(features).map(([category, data]) => (
                    <div key={category} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-lg">{category}</h3>
                          <p className="text-sm text-muted-foreground">{data.description}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const text = `${category}\n${data.description}\n\n${data.features.map((f, i) => `${i + 1}. ${f}`).join("\n")}`;
                            copyToClipboard(text, category);
                          }}
                        >
                          {copiedSection === category ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      <ul className="space-y-2">
                        {data.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <span className="text-primary font-mono text-xs bg-primary/10 px-1.5 py-0.5 rounded mt-0.5">
                              {idx + 1}
                            </span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default AdminArchitecturePage;
